/* istanbul ignore file */
import { useState } from 'react';
import { useAuthorization } from 'framework/authorization';
import { Button } from 'framework/ui/mui-builder';
import AssignmentFormDrawer from '../createupdate/AssignmentFormDrawer';
import { AssignmentDrawerMode } from '../createupdate/types';
import { Show } from 'framework/show';
import { ShowSection, SectionRow, ShowPageLayout } from 'framework/ui/layout';
import ViewAssignmentPageMenu from 'domain/staffing/assignment/Show/ViewAssignmentPageMenu';
import AssignmentEntityHeader from 'domain/staffing/assignment/shared/AssignmentEntityHeader';
import SingleAssignmentBreadcrumbs from 'domain/staffing/assignment/shared/SingleAssignmentBreadcrumbs';
import DeleteAssignmentButton from 'domain/staffing/assignment/shared/button/DeleteAssignmentButton';
import { BooleanField, DateField, PercentField } from 'framework/ui/field';
import { EmployeeLinkField, EmployeeTechnologyField } from 'domain/shared/employee';
import {
  AssignmentMembershipRoleField,
  AssignmentStatusField,
  AssignmentTargetLinkField,
  CREATE_ASSIGNMENT,
  PositionInAssignmentField,
  UPDATE_ASSIGNMENT,
  useAssignmentService,
} from 'domain/shared/assignment';
import { useAssignmentId } from 'domain/staffing/assignment/assignmentRouting';
import { BudgetLinkField } from 'domain/shared/budget';
import AssigneeField from 'domain/staffing/assignment/shared/field/AssigneeField';
import AssignmentTypeField from 'domain/staffing/assignment/shared/field/AssignmentTypeField';

const ViewAssignment = () => {
  const id = useAssignmentId();
  const { getAssignmentById } = useAssignmentService();
  const { isGranted } = useAuthorization();
  const [drawerMode, setDrawerMode] = useState<AssignmentDrawerMode>();
  const [version, setVersion] = useState(0);
  const drawer = drawerMode && (
    <AssignmentFormDrawer
      mode={drawerMode}
      assignmentId={id}
      onClose={() => setDrawerMode(undefined)}
      onSuccess={() => {
        if (drawerMode === AssignmentDrawerMode.EDIT) setVersion(value => value + 1);
      }}
    />
  );

  return (
    <>
      <Show key={version} getDataMethod={() => getAssignmentById(id)}>
        <ShowPageLayout
          headerButtons={[
            ...(isGranted(UPDATE_ASSIGNMENT)
              ? [
                  <Button
                    key="edit-assignment"
                    variant="outlined"
                    onClick={() => setDrawerMode(AssignmentDrawerMode.EDIT)}
                  >
                    Edit
                  </Button>,
                ]
              : []),
            ...(isGranted(CREATE_ASSIGNMENT)
              ? [
                  <Button
                    key="copy-assignment"
                    variant="outlined"
                    onClick={() => setDrawerMode(AssignmentDrawerMode.COPY)}
                  >
                    Copy
                  </Button>,
                ]
              : []),
            <DeleteAssignmentButton key="delete-assignment" />,
          ]}
          menu={<ViewAssignmentPageMenu />}
          breadcrumbs={<SingleAssignmentBreadcrumbs />}
          entityHeader={<AssignmentEntityHeader />}
          hasSeparateSections
        >
          <ShowSection title="Assignment information">
            <SectionRow>
              <AssignmentTargetLinkField
                label="Target"
                source="target.id"
                nameSource="target.displayName"
                typeSource="target.type"
                serviceCatalogIdSource="target.targetId"
              />
              <EmployeeLinkField source="ownerId" nameSource="ownerName" label="Owner" />
              <AssigneeField source="assignmentType" label="Assignee" />
              <PositionInAssignmentField label="Worker position" source="assignmentType" />
              <AssignmentMembershipRoleField label="Membership role" source="membershipRole" />
              <EmployeeTechnologyField label="Technology" source="technology.id" />
              <EmployeeLinkField source="backupForEmployeeId" nameSource="backupForEmployeeName" label="Backup for" />
              <BooleanField source="bench" label="Bench" />
              <AssignmentStatusField label="Status" source="status" />
              <BooleanField source="isSpof" label="SPOF" />
            </SectionRow>
            <SectionRow>
              <DateField source="from" label="Start date" />
              <DateField source="to" label="End date" />
              <PercentField source="minPercent" label="Min percent" />
              <PercentField source="maxPercent" label="Max percent" />
            </SectionRow>
            <SectionRow>
              <AssignmentTypeField source="assignmentType" label="Assignment type" />
              <BudgetLinkField label="Budget" source="budgetId" nameSource="budget.name" />
            </SectionRow>
          </ShowSection>
        </ShowPageLayout>
      </Show>
      {drawer}
    </>
  );
};

export default ViewAssignment;
